import React, { PropsWithChildren, useCallback, useState } from 'react';

import { omit } from 'lodash-es';
import { Platform } from 'react-native';

import { ISanityImageMetadata } from '@rbi-ctg/menu';

import Blurhash from './blurhash';
import { SvgOrImage } from './svgOrImage';
import { ImageSource, ImageStyle, LoadState, ResizeMode } from './types';
import { isSVG } from './utils';

const imageLoadingStyle: ImageStyle = {
  width: '100%',
  height: 0,
  opacity: 0.8,
};

interface IImageWithPreloadProps {
  testID?: string;
  cache: NonNullable<ImageSource['cache']>;
  format?: string;
  loadingStyle: ImageStyle;
  onLoad?: () => void;
  metadata?: ISanityImageMetadata;
  priority: NonNullable<ImageSource['priority']>;
  style: ImageStyle;
  uri: NonNullable<ImageSource['uri']>;
  accessibilityLabel?: string;
  resizeMode?: ResizeMode;
}

// have we encountered this URL before during this app's lifetime?
// this doesn't account for images that are in cache from previous sessions
// there is no way to determine this with react-native-fast-image (it doesn't expose its cache)
const imageIsInCacheSet = new Set();

const ImageWithPreload: React.FC<PropsWithChildren<IImageWithPreloadProps>> = ({
  format,
  loadingStyle,
  onLoad,
  metadata,
  style,
  uri,
  accessibilityLabel,
  ...props
}) => {
  const isWeb = Platform.OS === 'web';

  const hasPreloadImage = !!(metadata?.blurHash || metadata?.lqip);

  const [loadState, setLoadState] = useState<LoadState>(
    hasPreloadImage && !isWeb ? LoadState.loading : LoadState.loaded
  );

  const wrappedOnLoad = useCallback(() => {
    setLoadState(LoadState.loaded);
    imageIsInCacheSet.add(uri);
    onLoad?.();
  }, [onLoad, uri]);

  const resolvedStyle = loadState === LoadState.loaded ? style : loadingStyle ?? style;

  const isPreloadFormatSvg = metadata?.lqip?.startsWith('data:image/svg');
  const isFinalFormatSvg = isSVG(format);

  const shouldShowPreloadImage =
    !imageIsInCacheSet.has(uri) &&
    !isWeb && // DO NOT ENABLE FOR WEB WITHOUT PROVIDING A BLURHASH IMPLEMENTATION
    loadState === LoadState.loading &&
    hasPreloadImage &&
    (!isFinalFormatSvg || !!isPreloadFormatSvg);

  return (
    <>
      <SvgOrImage
        accessibilityLabel={accessibilityLabel}
        isSVG={isFinalFormatSvg}
        uri={uri}
        style={loadState === LoadState.loaded ? resolvedStyle : imageLoadingStyle}
        onLoad={wrappedOnLoad}
        {...props}
      />
      {shouldShowPreloadImage && metadata?.lqip && (
        <SvgOrImage
          accessibilityLabel={accessibilityLabel}
          isSVG={isPreloadFormatSvg ?? false}
          uri={metadata?.lqip}
          style={resolvedStyle}
          {...props}
        />
      )}
      {shouldShowPreloadImage && metadata?.blurHash && (
        <Blurhash
          accessibilityLabel={accessibilityLabel}
          blurhash={metadata?.blurHash}
          style={resolvedStyle}
          {...omit(props, 'children')}
        />
      )}
    </>
  );
};

export default ImageWithPreload;
